// @edemics/lib/api — the shared axios instance every service uses.
//
// Runtime-agnostic: no `process.env` reads at module load (which crashes
// in the browser under Vite). Instead the default baseURL is a safe
// production URL, and each consuming app calls `setApiBaseURL(...)` from
// its entry file with the URL it wants.
//
//   // tenant-app/src/main.tsx
//   import { setApiBaseURL } from "@edemics/lib/api"
//   setApiBaseURL(import.meta.env.VITE_API_URL || "https://bknd.edemics.com")
//
//   // landing/app/layout.tsx (future Next app)
//   import { setApiBaseURL } from "@edemics/lib/api"
//   setApiBaseURL(process.env.NEXT_PUBLIC_API_URL || "https://bknd.edemics.com")
//
// Ordering: apps MUST call setApiBaseURL before the first API request.
// Since services are only invoked from route components (lazy-loaded), and
// main.tsx runs before any route mounts, the setter always fires first.

import axios from "axios"
import { getSubdomainFromHostname, getTenantHostname } from "./subdomain"

const DEFAULT_BASE_URL = "https://bknd.edemics.com"

export const api = axios.create({
  baseURL: DEFAULT_BASE_URL,
})

/** Override the base URL before any API request fires. Call from the app entry. */
export function setApiBaseURL(url: string): void {
  api.defaults.baseURL = url
}

// Request interceptor — adds the auth token AND the tenant-subdomain header
// so the BE can resolve the tenant even before the user is authenticated
// (public/anonymous endpoints under /admission/*, /guest-access, and the
// tenant lookup itself). Read of the subdomain is cheap; hostname doesn't
// change over the life of a page load so a one-time module-level derivation
// would be equivalent — kept per-request for symmetry with the token read
// and so a testing harness can stub `window.location` between requests.
api.interceptors.request.use(
  (config) => {
    if (typeof window === "undefined") return config
    const token = window.sessionStorage.getItem("token")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    const subdomain = getSubdomainFromHostname(window.location.hostname)
    if (subdomain) {
      // BE (R1) reads this header on unauth surfaces to pick the right
      // tenant. For auth'd requests the JWT `tenantId` claim is the source
      // of truth; the header is redundant but safe (BE cross-checks the
      // two, and reject if they disagree — mismatch means a stolen JWT
      // is being replayed against a different tenant).
      config.headers["X-Tenant-Subdomain"] = subdomain
    }
    // Wave S (2026-09-10) — for BYO custom domains the subdomain header
    // is null (no edemics.com slug on the wire). Send the full hostname
    // in `X-Tenant-Host` so the BE can resolve the tenant regardless of
    // which routing path they arrived on. BE JwtStrategy (Wave S)
    // prefers this over X-Tenant-Subdomain when both are present.
    const tenantHost = getTenantHostname(window.location.hostname)
    if (tenantHost) {
      config.headers["X-Tenant-Host"] = tenantHost
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && typeof window !== "undefined") {
      window.sessionStorage.removeItem("token")
      window.sessionStorage.removeItem("user")
      window.location.href = "/login"
    }
    return Promise.reject(error)
  },
)
